/**
 * Local marker that records "this email is confirmed" so the banner stops
 * nagging immediately after the user clicks the confirmation link in their
 * inbox.
 *
 * Why this exists:
 *   The SDK's <c>useEnrichedAuth</c> exposes the user object straight from
 *   the cached JWT — the JWT was minted at sign-in time with
 *   <c>EmailConfirmed=false</c> and isn't reissued when the backend flips
 *   the flag during /confirm-email. Without this marker the banner would
 *   keep showing until the user signs out and back in.
 *
 *   The marker is purely cosmetic. The real authority is the backend flag
 *   stamped on the next JWT issue.
 *
 *   We key by lowercased email so a user who confirms on one tab sees the
 *   change reflected on every open tab via the storage event.
 */

const KEY_PREFIX = 'ccms:email-confirmed:';

function keyFor(email: string | null | undefined): string | null {
  if (!email) return null;
  const trimmed = email.trim().toLowerCase();
  if (!trimmed) return null;
  return `${KEY_PREFIX}${trimmed}`;
}

export function markEmailConfirmedLocally(email: string | null | undefined): void {
  const k = keyFor(email);
  if (!k) return;
  try {
    window.localStorage.setItem(k, '1');
    // Other open tabs (e.g. the home page that has the banner) listen on
    // 'storage' but it doesn't fire in the originating tab — dispatch a
    // synthetic event so the same-tab banner updates instantly.
    window.dispatchEvent(new CustomEvent('ccms:email-confirmed', { detail: { email: email?.toLowerCase() ?? null } }));
  } catch {
    /* private mode / quota — banner will just stay until next sign-in */
  }
}

export function isEmailConfirmedLocally(email: string | null | undefined): boolean {
  const k = keyFor(email);
  if (!k) return false;
  try {
    return window.localStorage.getItem(k) === '1';
  } catch {
    return false;
  }
}

/**
 * Cleared on sign-out so the next account that signs in on the same device
 * doesn't inherit a stale marker.
 */
export function clearEmailConfirmedMarkers(): void {
  try {
    const stale: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(KEY_PREFIX)) stale.push(k);
    }
    stale.forEach((k) => window.localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}
